import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { api } from "../api/client.js";
import { useLoading } from "../loading/LoadingContext.jsx";
import AppHeader from "../components/AppHeader.jsx";
import LoadingBar from "../components/LoadingBar.jsx";
import BrandFooter from "../components/BrandFooter.jsx";
import {
  budgetState,
  daysInMonth,
  isCurrentMonth,
  money,
  monthLong,
  monthShort,
  paceForecast,
  pctText,
  periodLabelLong,
  previousPeriod,
  sum,
} from "./spend/derive.js";
import { CATEGORIES, categoryOf, friendlyService } from "./summary/categories.js";

/**
 * Summary — the management view.
 *
 * One question per section, answered in plain words: how much are we
 * spending, is that more or less than last month, is anything over budget,
 * where does the money go, and what changed. No AWS service codes, usage
 * types or payer mechanics; those live on Spend details. Everything here is
 * read from the same endpoints as the Spend page, so the two never disagree.
 *
 * For a month in progress, comparisons with last month use the EXPECTED month
 * end, not the month to date. Comparing ten days against a whole month makes
 * every cost look like it fell, which is the opposite of what a reader needs.
 */

const CHANGE_COUNT = 5;
const TREND_MONTHS = 6;

function recentPeriods(count) {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
}

/** "4% more than" / "12% less than" / "about the same as" — null when there is nothing to compare with. */
function comparePhrase(current, previous) {
  if (!(previous > 0)) return null;
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 1) return { text: "about the same as", dir: "flat", pct };
  return { text: `${pctText(pct)} ${pct > 0 ? "more" : "less"} than`, dir: pct > 0 ? "up" : "down", pct };
}

function Change({ current, previous, suffix = "" }) {
  if (!(previous > 0) && current > 0) return <span className="sum-change sum-change-up">New{suffix}</span>;
  const c = comparePhrase(current, previous);
  if (!c) return null;
  if (c.dir === "flat") return <span className="sum-change">No change{suffix}</span>;
  return (
    <span className={`sum-change sum-change-${c.dir}`}>
      {c.dir === "up" ? "▲" : "▼"} {pctText(c.pct)}
      {suffix}
    </span>
  );
}

export default function Summary() {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const periods = useMemo(() => recentPeriods(12), []);
  const [period, setPeriod] = useState(periods[0]);
  const [data, setData] = useState(null);
  const [savings, setSavings] = useState(undefined); // undefined = loading, null = unavailable
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setErrorMsg(null);
    setData(null);
    start();
    (async () => {
      const [byAccount, daily, movers, trend] = await Promise.all([
        api.getCostByAccount(idToken, period, "previous"),
        api.getCostDaily(idToken, period).catch(() => null),
        api.getCostMovers(idToken, period, 200).catch(() => null),
        api.getCostTrend(idToken, TREND_MONTHS).catch(() => null),
      ]);
      // Per-account service lists are read from the nightly snapshot table,
      // so one call per account is cheap — no Cost Explorer charge.
      const services = await Promise.all(
        (byAccount.accounts || []).map((a) =>
          api.getCostByService(idToken, a.account_id, { period }, false, null, "previous").catch(() => null),
        ),
      );
      if (!cancelled) setData({ byAccount, daily, movers, trend, services });
    })()
      .catch((err) => !cancelled && setErrorMsg(err.message))
      .finally(stop);

    setSavings(undefined);
    api
      .getSavings(idToken, period)
      .then((r) => !cancelled && setSavings(r))
      .catch(() => !cancelled && setSavings(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, period]);

  const view = useMemo(() => (data ? derive(period, data) : null), [period, data]);
  const prev = previousPeriod(period);

  return (
    <>
      <LoadingBar />
      <AppHeader />
      <div className="control-rail summary-rail">
        <div className="rail-group">
          <label className="rail-label" htmlFor="summary-month">
            Month
          </label>
          <select id="summary-month" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periods.map((p) => (
              <option key={p} value={p}>
                {periodLabelLong(p)}
                {isCurrentMonth(p) ? " (this month)" : ""}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="ghost" onClick={() => window.print()}>
          Print or save as PDF
        </button>
      </div>

      <main className="summary-page">
        {errorMsg && <div className="error-banner">{errorMsg}</div>}
        {!view && !errorMsg && <div className="summary-loading">Preparing the summary…</div>}
        {view && (
          <>
            <Headline period={period} prev={prev} view={view} />
            <KeyFigures period={period} prev={prev} view={view} savings={savings} />
            <div className="summary-grid">
              <Categories prev={prev} view={view} />
              <AccountsPanel prev={prev} view={view} />
            </div>
            <div className="summary-grid">
              <TrendPanel view={view} />
              <ChangesPanel prev={prev} view={view} />
            </div>
            <p className="summary-footnote">
              Amounts are in US dollars at AWS list prices, before credits and discounts, and are refreshed nightly.
              {view.current && " Expected figures assume spending carries on at its current rate."} The AWS invoice is
              the final amount; see <Link to="/invoices">Invoices</Link>. For a day-by-day and per-service view, open{" "}
              <Link to="/spend">Spend details</Link>.
            </p>
          </>
        )}
        <BrandFooter className="spend-footer" />
      </main>
    </>
  );
}

// ---------------------------------------------------------------------------
// Derivation — everything the sections show, computed once per render.
// ---------------------------------------------------------------------------
function derive(period, { byAccount, daily, movers, trend, services }) {
  const current = isCurrentMonth(period);
  const days = daysInMonth(period);
  const accounts = byAccount.accounts || [];
  const untracked = byAccount.untracked || [];
  const untrackedTotal = sum(untracked.map((u) => Number(u.total) || 0));

  // Monthly totals come from the nightly snapshot — the same numbers as the
  // six-month chart and the Trend page, including accounts on the bill that
  // aren't registered — so the headline, the figures and the chart agree.
  // Only the month in progress uses the live daily series, which is fresher
  // than last night's snapshot and is what the forecast is built on.
  const series = (daily?.daily || []).map(Number);
  const snapshotTotal = sum(accounts.map((a) => Number(a.total) || 0)) + untrackedTotal;
  const trendTotal = (p) => {
    const row = (trend?.trend || []).find((t) => t.period === p);
    return row ? Number(row.total) || 0 : null;
  };
  const spent = current && daily ? sum(series) : (trendTotal(period) ?? snapshotTotal);
  const prevTotal =
    trendTotal(previousPeriod(period)) ?? sum(accounts.map((a) => Number(a.previous_total) || 0));

  let monthEnd = spent;
  let forecastNote = "Final figure";
  let range = null;
  if (current) {
    const aws = daily?.aws_forecast;
    if (aws?.month_end) {
      monthEnd = Number(aws.month_end);
      forecastNote = "AWS's own estimate";
      if (aws.month_end_lower && aws.month_end_upper) range = [aws.month_end_lower, aws.month_end_upper];
    } else {
      monthEnd = paceForecast(series, days, "7").monthEnd;
      forecastNote = "Based on the last 7 days";
    }
  }
  const growth = current && spent > 0 ? monthEnd / spent : 1;

  // Accounts, with expected month end and budget status.
  const accountRows = accounts
    .map((a) => {
      const total = Number(a.total) || 0;
      const expected = total * growth;
      return {
        id: a.account_id,
        name: a.account_name || a.account_id,
        total,
        expected,
        previous: Number(a.previous_total) || 0,
        budget: budgetState(expected, a.monthly_budget),
      };
    })
    .sort((x, y) => y.total - x.total);
  const budgeted = accountRows.filter((r) => r.budget);
  const overBudget = budgeted.filter((r) => r.budget.isOver);

  // Categories, summed across every registered account.
  const byCat = new Map(CATEGORIES.map((c) => [c.id, { ...c, amount: 0, previous: 0, services: new Map() }]));
  for (const svc of services) {
    for (const row of svc?.services || []) {
      const cat = byCat.get(categoryOf(row.service));
      const amount = Number(row.amount) || 0;
      cat.amount += amount;
      cat.previous += Number(row.previous_amount) || 0;
      cat.services.set(row.service, (cat.services.get(row.service) || 0) + amount);
    }
  }
  const categoryTotal = sum([...byCat.values()].map((c) => c.amount));
  const categories = [...byCat.values()]
    .filter((c) => c.amount >= 0.5 || c.previous >= 0.5)
    .map((c) => ({
      ...c,
      expected: c.amount * growth,
      share: categoryTotal > 0 ? c.amount / categoryTotal : 0,
      top: [...c.services.entries()]
        .filter(([, v]) => v >= 0.5)
        .sort((x, y) => y[1] - x[1])
        .slice(0, 4),
    }))
    .sort((x, y) => y.amount - x.amount);

  // Biggest changes: expected month end vs last month's total.
  const changes = (movers?.movers || [])
    .map((m) => {
      const now = (Number(m.amount) || 0) * growth;
      const before = Number(m.previous_amount) || 0;
      return { ...m, now, before, diff: now - before };
    })
    .filter((m) => Math.abs(m.diff) >= 1)
    .sort((x, y) => Math.abs(y.diff) - Math.abs(x.diff))
    .slice(0, CHANGE_COUNT);

  // Six-month trend; the current month is drawn as spent + expected rest.
  const trendRows = (trend?.trend || []).map((t) => {
    const isThis = t.period === period; // highlight the month being viewed
    return {
      period: t.period,
      actual: isThis ? spent : Number(t.total) || 0,
      expected: isThis ? monthEnd : Number(t.total) || 0,
      isThis,
    };
  });

  return {
    current,
    days,
    elapsed: series.length,
    spent,
    prevTotal,
    monthEnd,
    forecastNote,
    range,
    accountRows,
    budgeted,
    overBudget,
    categories,
    untrackedTotal,
    changes,
    trendRows,
  };
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------
function Headline({ period, prev, view }) {
  const month = monthLong(period);
  const lastMonth = monthLong(prev);
  const cmp = comparePhrase(view.current ? view.monthEnd : view.spent, view.prevTotal);

  const title = view.current
    ? `${month} is on track to cost about ${money(view.monthEnd)}${cmp ? `, ${cmp.text} ${lastMonth}` : ""}.`
    : `${month} cost ${money(view.spent)}${cmp ? `, ${cmp.text} ${lastMonth}` : ""}.`;
  const lede = view.current
    ? `${money(view.spent)} spent in the first ${view.elapsed} of ${view.days} days. ${lastMonth}'s total was ${money(view.prevTotal)}.`
    : `${lastMonth}'s total was ${money(view.prevTotal)}.`;

  let budgetLine = null;
  if (view.budgeted.length === 0) {
    budgetLine = {
      tone: "neutral",
      text: "No monthly budgets are set yet. An admin can add them on the Accounts page.",
    };
  } else if (view.overBudget.length === 0) {
    budgetLine = {
      tone: "good",
      text:
        view.budgeted.length === 1
          ? `${view.budgeted[0].name} is ${view.current ? "expected to stay" : "was"} within its budget.`
          : `All ${view.budgeted.length} accounts with a budget ${view.current ? "are expected to stay" : "stayed"} within it.`,
    };
  } else {
    const names = view.overBudget.map((r) => `${r.name} (${money(r.budget.over)} over)`).join(", ");
    budgetLine = {
      tone: "bad",
      text: `${view.overBudget.length} of ${view.budgeted.length} budgeted account${view.budgeted.length === 1 ? "" : "s"} ${
        view.current ? "expected to go" : "went"
      } over budget: ${names}.`,
    };
  }

  return (
    <section className="summary-headline">
      <p className="summary-period">
        {periodLabelLong(period)}
        {view.current ? `, day ${view.elapsed} of ${view.days}` : ""}
      </p>
      <h1>{title}</h1>
      <p className="summary-lede">{lede}</p>
      <p className={`summary-budget summary-budget-${budgetLine.tone}`}>{budgetLine.text}</p>
    </section>
  );
}

function KeyFigures({ period, prev, view, savings }) {
  const savingsValue = savings === undefined ? "…" : savings ? `${money(savings.total_monthly_savings)}` : "—";
  return (
    <section className="summary-figures" aria-label="Key figures">
      <div className="summary-figure">
        <div className="summary-figure-label">{view.current ? "Spent so far" : `Total for ${monthLong(period)}`}</div>
        <div className="summary-figure-value">{money(view.spent)}</div>
        <div className="summary-figure-note">
          {view.current ? `${view.elapsed} of ${view.days} days` : "Final figure"}
        </div>
      </div>
      <div className="summary-figure">
        <div className="summary-figure-label">{view.current ? "Expected by month end" : "Average per day"}</div>
        <div className="summary-figure-value">{money(view.current ? view.monthEnd : view.spent / view.days)}</div>
        <div className="summary-figure-note">
          {view.current
            ? `${view.forecastNote}${view.range ? `, likely ${money(view.range[0])}–${money(view.range[1])}` : ""}`
            : `Over ${view.days} days`}
        </div>
      </div>
      <div className="summary-figure">
        <div className="summary-figure-label">{monthLong(prev)} total</div>
        <div className="summary-figure-value">{money(view.prevTotal)}</div>
        <div className="summary-figure-note">
          <Change current={view.current ? view.monthEnd : view.spent} previous={view.prevTotal} />{" "}
          {view.current ? "expected change" : "change"}
        </div>
      </div>
      <Link to="/savings" className="summary-figure summary-figure-link">
        <div className="summary-figure-label">Possible savings</div>
        <div className="summary-figure-value summary-figure-good">
          {savingsValue}
          {savings ? <span className="summary-figure-unit"> a month</span> : null}
        </div>
        <div className="summary-figure-note">
          {savings
            ? `${savings.recommendation_count} AWS recommendation${savings.recommendation_count === 1 ? "" : "s"}. View savings`
            : savings === null
              ? "Not available right now"
              : "Checking AWS recommendations"}
        </div>
      </Link>
    </section>
  );
}

function Categories({ prev, view }) {
  const max = Math.max(...view.categories.map((c) => c.amount), 1);
  return (
    <section className="summary-panel">
      <div className="summary-panel-head">
        <h2>Where the money goes</h2>
        <p>By type of service, all accounts. Select a row to see what's in it.</p>
      </div>
      {view.categories.length === 0 ? (
        <p className="pane-empty">No cost breakdown for this month yet.</p>
      ) : (
        <ul className="sum-list">
          {view.categories.map((c) => (
            <li key={c.id}>
              <details className="sum-cat">
                <summary>
                  <span className="sum-row">
                    <span className="sum-name">
                      {c.label}
                      <span className="sum-sub">{c.note}</span>
                    </span>
                    <span className="sum-figs">
                      <span className="sum-amount">{money(c.amount)}</span>
                      <span className="sum-share">{Math.round(c.share * 100)}%</span>
                    </span>
                  </span>
                  <span className="sum-bar">
                    <span className="sum-bar-fill" style={{ width: `${(c.amount / max) * 100}%` }} />
                  </span>
                  <span className="sum-meta">
                    <Change current={view.current ? c.expected : c.amount} previous={c.previous} />{" "}
                    {view.current ? `expected vs ${monthShort(prev)}` : `vs ${monthShort(prev)}`}
                  </span>
                </summary>
                <ul className="sum-detail">
                  {c.top.map(([svc, amt]) => (
                    <li key={svc}>
                      <span>{friendlyService(svc)}</span>
                      <span>{money(amt)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      )}
      {view.untrackedTotal >= 0.5 && (
        <p className="summary-panel-note">
          A further {money(view.untrackedTotal)} comes from accounts on the bill that aren't registered here yet, so it
          isn't broken down.
        </p>
      )}
    </section>
  );
}

function AccountsPanel({ prev, view }) {
  const [showIdle, setShowIdle] = useState(false);
  // No cost this month and none last month: nothing for a reader to act on.
  const idle = (r) => Math.abs(r.total) < 0.005 && Math.abs(r.previous) < 0.005;
  const idleCount = view.accountRows.filter(idle).length;
  const rows = showIdle ? view.accountRows : view.accountRows.filter((r) => !idle(r));
  const max = Math.max(...rows.map((r) => r.total), 1);
  return (
    <section className="summary-panel">
      <div className="summary-panel-head">
        <h2>By account</h2>
        <p>
          {view.current ? "Spent so far, with the expected month end and budget." : "Total for the month and budget."}
        </p>
      </div>
      <ul className="sum-list">
        {rows.map((r) => (
          <li key={r.id} className="sum-acct">
            <span className="sum-row">
              <span className="sum-name">{r.name}</span>
              <span className="sum-amount">{money(r.total)}</span>
            </span>
            <span className="sum-bar">
              <span className="sum-bar-fill sum-bar-fill-muted" style={{ width: `${(r.total / max) * 100}%` }} />
            </span>
            <span className="sum-meta">
              {view.current ? `Expected ${money(r.expected)}` : <Change current={r.total} previous={r.previous} />}
              {!view.current && ` vs ${monthShort(prev)}`}
              {r.budget ? (
                <span className={`sum-chip ${r.budget.isOver ? "sum-chip-bad" : "sum-chip-good"}`}>
                  {r.budget.isOver
                    ? `${money(r.budget.over)} over ${money(r.budget.limit)} budget`
                    : `Within ${money(r.budget.limit)} budget`}
                </span>
              ) : (
                <span className="sum-chip">No budget</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {idleCount > 0 && (
        <p className="sum-idle">
          {showIdle
            ? `Including ${idleCount} ${idleCount === 1 ? "account" : "accounts"} with no cost.`
            : `${idleCount} ${idleCount === 1 ? "account" : "accounts"} with no cost not shown.`}{" "}
          <button type="button" className="pane-idle-toggle" onClick={() => setShowIdle((v) => !v)}>
            {showIdle ? "Hide" : "Show"}
          </button>
        </p>
      )}
    </section>
  );
}

function TrendPanel({ view }) {
  const rows = view.trendRows;
  const max = Math.max(...rows.map((r) => r.expected), 1);
  return (
    <section className="summary-panel">
      <div className="summary-panel-head">
        <h2>Last {rows.length} months</h2>
        <p>Total AWS cost per month{view.current ? "; this month shows spent so far plus expected" : ""}.</p>
      </div>
      {rows.length === 0 ? (
        <p className="pane-empty">No monthly history yet.</p>
      ) : (
        <div
          className="sum-trend"
          role="img"
          aria-label={rows.map((r) => `${monthShort(r.period)} ${money(r.expected)}`).join(", ")}
        >
          {rows.map((r) => (
            <div key={r.period} className={`sum-trend-col${r.isThis ? " sum-trend-col-this" : ""}`}>
              <span className="sum-trend-value">{money(r.expected)}</span>
              <span className="sum-trend-track">
                <span className="sum-trend-expected" style={{ height: `${(r.expected / max) * 100}%` }} />
                <span className="sum-trend-actual" style={{ height: `${(r.actual / max) * 100}%` }} />
              </span>
              <span className="sum-trend-label">{monthShort(r.period)}</span>
            </div>
          ))}
        </div>
      )}
      {view.current && rows.some((r) => r.isThis) && (
        <div className="chart-legend sum-trend-legend">
          <span className="legend-item">
            <span className="legend-swatch" />
            Spent
          </span>
          <span className="legend-item">
            <span className="legend-swatch legend-swatch-forecast" />
            Expected rest of month
          </span>
        </div>
      )}
    </section>
  );
}

function ChangesPanel({ prev, view }) {
  const last = monthLong(prev);
  return (
    <section className="summary-panel">
      <div className="summary-panel-head">
        <h2>Biggest changes</h2>
        <p>{view.current ? `Expected month end compared with ${last}.` : `Compared with ${last}.`}</p>
      </div>
      {view.changes.length === 0 ? (
        <p className="pane-empty">Nothing changed by more than a dollar.</p>
      ) : (
        <ol className="sum-changes">
          {view.changes.map((m) => {
            const up = m.diff > 0;
            const isNew = m.before < 0.5;
            return (
              <li key={`${m.account_id}|${m.service}`}>
                <span className={`sum-change-mark ${up ? "sum-change-up" : "sum-change-down"}`} aria-hidden="true">
                  {up ? "▲" : "▼"}
                </span>
                <span>
                  <strong>{friendlyService(m.service)}</strong> in {m.account_name}
                  <span className="sum-sub">
                    {isNew
                      ? `New this month, ${view.current ? "expected " : ""}${money(m.now)}`
                      : `${up ? "Up" : "Down"} ${money(Math.abs(m.diff))} (${pctText((m.diff / m.before) * 100)}), ${money(m.before)} → ${view.current ? "about " : ""}${money(m.now)}`}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
